import { getDiagnosticBenchmarks, getDiagnosticDefaultsForNiche } from '../../data/businessNicheDirectory';
import { DEFAULT_DIAGNOSTIC_PERIOD_ID, getDiagnosticPeriodDays } from '../../data/diagnosticPeriodDirectory';

export { getDiagnosticDefaultsForNiche };

export const DEFAULT_DIAGNOSTIC_INPUTS = {
  leads: 50,
  conv: 20,
  sales: 60,
  ltvQ: 5,
  churn: 5,
  revenue: 10000000,
  checkDepth: 1,
  margin: 60,
  budget: 300000,
  cacShare: 30,
  revenueTarget: 15000000,
};

export const DEFAULT_COMBO_SLIDERS = { l: 20, c: 20, s: 20, d: 20, i: 20 };

export const clampNumber = (value, min, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
};

export const formatMoney = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString(locale)} ₸`;
};

export const formatDecimal = (value, locale = 'ru-RU', digits = 2) => {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatCount = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString(locale);
};

export const getEffortLevel = (pct) => {
  if (pct === null || !Number.isFinite(pct)) return null;
  if (pct <= 0) return 'ok';
  const abs = Math.abs(pct);
  if (abs < 30) return 'low';
  if (abs < 70) return 'mid';
  return 'high';
};

/** Приоритет при равном % роста: что проще внедрить на практике */
const LEVER_TIE_PRIORITY = { d: 0, s: 1, p: 2, i: 3, l: 4, c: 5 };

const getEffortLevelForLever = (row) => {
  if (row.impossible) return 'impossible';
  if (row.pct === null) return null;
  if (row.pct <= 0) return 'ok';
  if (row.key === 'd' && row.abs != null && row.abs <= 2) return 'low';
  return getEffortLevel(row.pct);
};

const assignEaseRanks = (rows) => {
  const viable = rows.filter((r) => !r.impossible && r.pct != null && r.pct > 0);
  if (!viable.length) {
    return rows.map((row) => ({ ...row, easeRank: null }));
  }

  const minPct = Math.min(...viable.map((r) => r.pct));
  const tier1 = viable
    .filter((r) => Math.abs(r.pct - minPct) <= 0.5)
    .sort((a, b) => LEVER_TIE_PRIORITY[a.key] - LEVER_TIE_PRIORITY[b.key])
    .slice(0, 3);

  return rows.map((row) => {
    const idx = tier1.findIndex((r) => r.key === row.key);
    return { ...row, easeRank: idx >= 0 ? idx + 1 : null };
  });
};

export const calculateMarketingDiagnostic = (inputs, nicheId = null, periodId = DEFAULT_DIAGNOSTIC_PERIOD_ID) => {
  const benchmarks = getDiagnosticBenchmarks(nicheId);
  const periodDays = getDiagnosticPeriodDays(periodId);
  const L = clampNumber(inputs.leads, 0);
  const convPct = clampNumber(inputs.conv, 0.1, 100);
  const C = convPct / 100;
  const sales = clampNumber(inputs.sales, 0);
  const ltvQ = clampNumber(inputs.ltvQ, 1);
  const margin = clampNumber(inputs.margin, 1, 99);
  const cacShare = clampNumber(inputs.cacShare, 5, 80);
  const budget = clampNumber(inputs.budget, 0);
  const R = clampNumber(inputs.revenue, 1);
  const Rt = clampNumber(inputs.revenueTarget, 1);
  const depth = clampNumber(inputs.checkDepth, 1);

  const CL = L * C;
  const P = sales > 0 ? R / sales : 0;
  const I = depth > 0 && P > 0 ? P / depth : 0;

  const dataError = sales <= 0 ? 'DATA_ERROR_SALES' : null;

  const CPL = L > 0 ? budget / L : 0;
  const CAC = CL > 0 ? budget / CL : 0;
  const LTV = P > 0 ? P * ltvQ : 0;
  const ltvMargin = LTV * (margin / 100);
  const maxCAC = ltvMargin * (cacShare / 100);
  const maxCPL = C > 0 ? maxCAC * C : 0;
  const ROMI = CAC > 0 ? ((ltvMargin - CAC) / CAC) * 100 : 0;
  const ltvCac = CAC > 0 ? LTV / CAC : 0;
  const budgetShare = R > 0 ? (budget / R) * 100 : 0;
  const profit = R * (margin / 100);
  const profitPerSale = sales > 0 ? profit / sales : 0;
  const salesPerDay = periodDays > 0 ? sales / periodDays : 0;

  const targetGrowthPct = R > 0 ? ((Rt / R - 1) * 100) : 0;
  const targetGrowthAbs = Rt - R;

  let adsInsight = null;
  if (CPL > 0 && maxCPL > 0) {
    if (CPL > maxCPL) {
      adsInsight = {
        text: `Стоимость заявки ${formatMoney(CPL)} превышает максимум ${formatMoney(maxCPL)}. Реклама работает в минус.`,
        tone: 'bad',
      };
    } else {
      const reserve = ((maxCPL - CPL) / maxCPL) * 100;
      adsInsight = {
        text: `Стоимость заявки ${formatMoney(CPL)} ниже максимума ${formatMoney(maxCPL)}. Запас ${Math.round(reserve)}% — можно масштабировать.`,
        tone: 'good',
      };
    }
  }

  let roiInsight = null;
  if (ltvCac > 0) {
    if (ltvCac < benchmarks.ltvCacWarn) {
      roiInsight = {
        text: `LTV/CAC ${ltvCac.toFixed(1)}× — критически низко. Норма для вашей ниши от ${benchmarks.ltvCacGood}×.`,
        tone: 'bad',
      };
    } else if (ltvCac < benchmarks.ltvCacGood) {
      roiInsight = {
        text: `LTV/CAC ${ltvCac.toFixed(1)}× — ниже нормы (${benchmarks.ltvCacGood}×). Запаса нет.`,
        tone: 'warn',
      };
    } else {
      roiInsight = { text: `LTV/CAC ${ltvCac.toFixed(1)}× — хорошо. Привлечение окупается.`, tone: 'good' };
    }
  }

  let budgetInsight = null;
  if (budgetShare > 0 && nicheId) {
    if (budgetShare > benchmarks.budgetShareMax) {
      budgetInsight = {
        text: `Доля рекламы ${budgetShare.toFixed(1)}% выше типичной для этой ниши (${benchmarks.budgetShareMax}%). Проверьте эффективность каналов.`,
        tone: 'warn',
      };
    } else if (budgetShare < benchmarks.budgetShareMin) {
      budgetInsight = {
        text: `Доля рекламы ${budgetShare.toFixed(1)}% ниже типичной (${benchmarks.budgetShareMin}%) — возможно, недоинвестируете в рост.`,
        tone: 'neutral',
      };
    }
  }

  const levers = buildDiagnosticLevers({ L, CL, C, P, I, depth, sales, R, Rt });

  return {
    L, CL, C, convPct, sales, P, I, depth, ltvQ, margin, cacShare, budget, R, Rt,
    CPL, CAC, LTV, ltvMargin, maxCAC, maxCPL, ROMI, ltvCac, budgetShare,
    profit, profitPerSale, salesPerDay, periodDays,
    targetGrowthPct, targetGrowthAbs, dataError,
    adsInsight, roiInsight, budgetInsight, levers, benchmarks,
    formulas: {
      cpl: CPL > 0 ? `${formatMoney(budget)} ÷ ${formatCount(L)}` : null,
      cr: C > 0 ? `${formatCount(Math.round(CL))} ÷ ${formatCount(L)}` : null,
      cac: CAC > 0 ? `${formatMoney(budget)} ÷ ${formatCount(Math.round(CL))}` : null,
      maxCpl: maxCAC > 0 ? `${formatMoney(maxCAC)} × ${(C * 100).toFixed(0)}%` : null,
      avgCheck: P > 0 ? `${formatMoney(R)} ÷ ${formatCount(sales)}` : null,
      itemPrice: I > 0 ? `${formatMoney(P)} ÷ ${formatDecimal(depth)}` : null,
      clients: CL > 0 ? `${formatCount(L)} × ${convPct.toFixed(1)}%` : null,
      salesPerDay: salesPerDay > 0 ? `${formatCount(sales)} ÷ ${periodDays}` : null,
      ltv: LTV > 0 ? `${formatMoney(P)} × ${ltvQ}` : null,
      maxCac: maxCAC > 0 ? `${formatMoney(LTV)} × ${margin}% × ${cacShare}%` : null,
      romi: CAC > 0 ? `(${formatMoney(ltvMargin)} − ${formatMoney(CAC)}) ÷ ${formatMoney(CAC)}` : null,
      ltvCac: ltvCac > 0 ? `${formatMoney(LTV)} ÷ ${formatMoney(CAC)}` : null,
      budgetShare: R > 0 ? `${formatMoney(budget)} ÷ ${formatMoney(R)}` : null,
      profit: profit > 0 ? `${formatMoney(R)} × ${margin}%` : null,
      profitPerSale: profitPerSale > 0 ? `${formatMoney(profit)} ÷ ${formatCount(sales)}` : null,
    },
  };
};

export const buildDiagnosticLevers = ({ L, C, P, I, depth, sales, R, Rt }) => {
  if (!R || !Rt || !P || !I || !depth) return [];

  const growthK = Rt / R;
  const growthPct = (growthK - 1) * 100;
  const unitRev = I * depth;

  let L_need = null; let L_pct = null; let L_abs = null;
  if (L > 0) {
    const need = L * growthK;
    L_need = Math.max(0, need);
    L_pct = growthPct;
    L_abs = need - L;
  }

  let C_need = null; let C_pct = null; let C_abs = null; let C_impossible = false;
  if (C > 0) {
    const need = C * growthK;
    C_pct = growthPct;
    C_abs = (need - C) * 100;
    if (need > 1) {
      C_impossible = true;
    } else {
      C_need = need;
    }
  }

  let P_need = null; let P_pct = null; let P_abs = null;
  if (sales > 0) {
    const need = Rt / sales;
    P_need = Math.max(0, need);
    P_pct = P > 0 ? ((need - P) / P) * 100 : null;
    P_abs = need - P;
  }

  let S_need = null; let S_pct = null; let S_abs = null;
  if (unitRev > 0) {
    const need = sales * growthK;
    S_need = Math.max(0, need);
    S_pct = sales > 0 ? growthPct : null;
    S_abs = need - sales;
  }

  let D_need = null; let D_pct = null; let D_abs = null;
  if (sales > 0 && I > 0) {
    const need = depth * growthK;
    D_need = Math.max(1, need);
    D_pct = depth > 0 ? growthPct : null;
    D_abs = need - depth;
  }

  let I_need = null; let I_pct = null; let I_abs = null;
  if (sales > 0 && depth > 0) {
    const need = I * growthK;
    I_need = Math.max(0, need);
    I_pct = I > 0 ? growthPct : null;
    I_abs = need - I;
  }

  const rows = [
    { key: 'l', dot: 'dl', name: 'Лиды', desc: 'входящие обращения', cur: L, need: L_need, pct: L_pct, abs: L_abs, impossible: false },
    { key: 'c', dot: 'dc', name: 'Конверсия', desc: 'лид → сделка', cur: C * 100, need: C_need != null ? C_need * 100 : null, pct: C_pct, abs: C_abs, impossible: C_impossible },
    { key: 's', dot: 'dq', name: 'Продажи / чеки', desc: 'больше сделок за период', cur: sales, need: S_need, pct: S_pct, abs: S_abs, impossible: false },
    { key: 'p', dot: 'dp', name: 'Средняя сумма покупки', desc: 'клиенты платят больше за раз', cur: P, need: P_need, pct: P_pct, abs: P_abs, impossible: false },
    { key: 'd', dot: 'dd', name: 'Глубина чека', desc: 'больше позиций в одном чеке', cur: depth, need: D_need, pct: D_pct, abs: D_abs, impossible: false },
    { key: 'i', dot: 'di', name: 'Средняя стоимость позиции', desc: 'дороже каждая позиция в чеке', cur: I, need: I_need, pct: I_pct, abs: I_abs, impossible: false },
  ];

  const ranked = assignEaseRanks(rows);

  return ranked.map((row) => ({
    ...row,
    effort: getEffortLevelForLever(row),
    goalReached: row.pct !== null && row.pct <= 0,
  }));
};

export const calculateCombo = (inputs, sliders, nicheId = null, periodId = DEFAULT_DIAGNOSTIC_PERIOD_ID) => {
  const d = calculateMarketingDiagnostic(inputs, nicheId, periodId);
  const { L, CL, sales, C, depth, I, Rt } = d;

  const slL = clampNumber(sliders.l, 0, 200);
  const slC = clampNumber(sliders.c, 0, 100);
  const slS = clampNumber(sliders.s, 0, 100);
  const slD = clampNumber(sliders.d, 0, 100);
  const slI = clampNumber(sliders.i, 0, 100);

  const newL = L * (1 + slL / 100);
  const newC = Math.min(C * (1 + slC / 100), 1);
  const newDepth = depth * (1 + slD / 100);
  const newI = I * (1 + slI / 100);
  const unitRev = newDepth * newI;

  const newCL = newL * newC;
  const acqSales = CL > 0 ? Math.min(CL, sales) : 0;
  const repeatSales = Math.max(0, sales - acqSales);
  const newAcqSales = CL > 0 ? acqSales * (newCL / CL) : newCL;
  const newRepeatSales = repeatSales;
  const newSales = (newAcqSales + newRepeatSales) * (1 + slS / 100);
  const newRev = newSales * unitRev;
  const gap = newRev - Rt;

  return {
    sliders: { l: slL, c: slC, s: slS, d: slD, i: slI },
    newL, newC, newCL, newSales, newDepth, newI, newRev, gap,
    reached: gap >= 0,
    formula: `${formatDecimal(newSales)} × ${formatDecimal(newDepth)} × ${formatMoney(newI)}`,
    leverRows: [
      { key: 'l', cur: L, new: newL, pct: slL },
      { key: 'c', cur: C * 100, new: newC * 100, pct: slC },
      { key: 's', cur: sales, new: newSales, pct: slS },
      { key: 'd', cur: depth, new: newDepth, pct: slD },
      { key: 'i', cur: I, new: newI, pct: slI },
    ],
  };
};

export const HOW_STEPS = [
  { num: '1', title: 'Каждый пункт имеет вес.', text: '«Да» = 100% балла, «Частично» = 50%, «Нет» = 0%. Итог — сумма взвешенных баллов.' },
  { num: '2', title: 'Пороги готовности:', text: 'до 50% — рано, 50–79% — доработать, 80%+ — можно запускать тест.' },
  { num: '3', title: 'Список «Сделать в первую очередь»', text: 'показывает пункты с ответом «Нет» и «Частично» по приоритету веса.' },
  { num: '4', title: 'Стратегия:', text: 'найдите рычаг с наименьшим % изменения — это самый быстрый путь. Но оптимальный сценарий — небольшой рост по всем четырём одновременно.' },
];
